import { useCallback, useMemo, useState } from 'react'

import { Box, Button, HStack, IconButton, Menu, Portal, Spacer, Text, VStack } from '@chakra-ui/react'
import { ChevronDownIcon, ResetIcon } from '@editor/assets/icons'
import { INPUT_GROUP_STYLES } from '@editor/config/styles'
import { usePrintScale } from '@editor/hooks/usePrintScale'
import { LineStyle } from '@editor/interfaces/canvas'
import { DrawingStyle, DrawingStyles, DrawingType } from '@editor/interfaces/drawing'
import { AVAILABLE_STYLES, MAX_TEXT_OFFSET, MIN_TEXT_OFFSET } from '@editor/pages/projects/blueprint-editor/constants'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { DEFAULT_STYLES } from '@editor/pages/projects/blueprint-editor/definitions'
import {
  updateDefaultStyles,
  updateDrawingStyles,
  updateWorkingStyles,
} from '@editor/pages/projects/blueprint-editor/store/drawings'
import { Tools } from '@editor/pages/projects/blueprint-editor/tools/setup'
import CollapsePanel from '@editor/pages/projects/editor/infoPanels/components/CollapsePanel'
import { mmToPointsWithScale, pointsToMmWithScale } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCurrentFillColor, setCurrentStrokeColor, setCurrentTextColor } from '@editor/store/tool'
import { RangeInput } from '@infraloop/common'
import { intersection } from 'lodash'
import { useSelector } from 'react-redux'

import ColorSetting from './ColorSetting'

const StylesPanel = () => {
  // Contexts
  const { shouldPreventExitEditingMode } = useBlueprintEditorContext()

  // Hooks
  const { currentPrintScale } = usePrintScale()

  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)
  const defaultStyles = useSelector((state: RootState) => state.blueprint.drawings.defaultStyles)
  const workingStyles = useSelector((state: RootState) => state.blueprint.drawings.workingStyles)
  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)

  // State
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isManualEditingStrokeSize, setIsManualEditingStrokeSize] = useState(false)
  const [isManualEditingTextSize, setIsManualEditingTextSize] = useState(false)
  const [isManualEditingTextOffset, setIsManualEditingTextOffset] = useState(false)

  // Derived values
  const currentTool = Tools.find((tool) => tool.key === selectedTool)

  /**
   * Get the UUIDs of selected drawings based on the selected element IDs.
   * This is to avoid including non-drawing UUIDs in the selection.
   */
  const selectedDrawingUuids = useMemo(
    () =>
      selectedElementIds.reduce((uuids, id) => {
        if (drawings.find((drawing) => drawing.drawing_parameters.uuid === id)) {
          uuids.push(id)
        }
        return uuids
      }, [] as string[]),
    [drawings, selectedElementIds],
  )

  /**
   * Get the first selected drawing
   */
  const firstSelectedDrawing = useMemo(() => {
    const adjustingId = editingElementId || workingElementId

    // If there are adjusting element, use that
    if (adjustingId) {
      return drawings.find((drawing) => drawing.drawing_parameters.uuid === adjustingId)
    }

    // If there are selected elements, use their styles; otherwise, use default styles
    if (selectedDrawingUuids.length > 0) {
      // Only need the first selected element's styles.
      // If they have multiple selected, any changes will apply to all of them.
      return drawings.find((drawing) => selectedDrawingUuids[0] === drawing.drawing_parameters.uuid)
    }

    return null
  }, [drawings, selectedDrawingUuids, editingElementId, workingElementId])

  /**
   * Get the styles to be used in the panel.
   * If there is a selected drawing, use its styles; otherwise, use default styles.
   */
  const currentStyles = useMemo(() => {
    if (workingElementId && workingStyles) {
      return workingStyles
    }

    if (firstSelectedDrawing) {
      return firstSelectedDrawing.drawing_parameters.styles || defaultStyles
    }

    return defaultStyles
  }, [defaultStyles, workingStyles, workingElementId, firstSelectedDrawing])

  /**
   * Figure out what styles are available for the current tool or selected elements.
   */
  const availableStyles = useMemo(() => {
    let type: DrawingType | undefined
    // If there is a selected drawing, use its available styles
    if (firstSelectedDrawing) {
      type = firstSelectedDrawing.drawing_type
    }

    if (currentTool?.drawingType) {
      type = currentTool.drawingType
    }

    return type ? AVAILABLE_STYLES[type] || [] : []
  }, [firstSelectedDrawing, currentTool])

  const hasTextStyles = useMemo(
    () => !!intersection(availableStyles, [DrawingStyle.TextColor, DrawingStyle.TextSize]).length,
    [availableStyles],
  )

  /**
   * Check if the styles are to be applied to a polyline.
   * Polyline has a different behavior for stroke width.
   * It cannot be 0 thickness, otherwise nothing will be rendered.
   */
  const isPolyline = useMemo(() => {
    if (firstSelectedDrawing) {
      return firstSelectedDrawing.drawing_type === DrawingType.Polyline
    }

    return currentTool?.drawingType === DrawingType.Polyline
  }, [firstSelectedDrawing, currentTool])

  /**
   * Handle update of drawing styles.
   * This will update the drawing document itself.
   */
  const handleUpdateDrawingStyles = useCallback(
    (styles: DrawingStyles) => {
      // If there's a drawing selected, update its styles
      if (selectedElementIds.length > 0) {
        // Dispatch an action to update the styles of the selected drawings
        dispatch(updateDrawingStyles({ uuids: selectedDrawingUuids, styles }))
        return
      }

      // Update existing editing/working element if any
      const adjustingId = editingElementId || workingElementId
      const drawing = drawings.find((d) => d.drawing_parameters.uuid === adjustingId)
      if (adjustingId && drawing) {
        // If there's an element being edited, update its styles
        dispatch(updateDrawingStyles({ uuids: [adjustingId], styles }))
        return
      }

      // If the current tool is one that does not add drawing directly to document,
      // but has working styles (e.g., Shapes tools), update the working styles instead
      if (adjustingId?.startsWith('working')) {
        dispatch(updateWorkingStyles(styles))
        return
      }

      // If not, just update the default styles
      dispatch(updateDefaultStyles(styles))
    },
    [selectedElementIds, selectedDrawingUuids, editingElementId, workingElementId, drawings, dispatch],
  )

  if (!availableStyles.length) return null

  return (
    <Box data-testid="settings-panel" className="panel" data-prevent-exit-editing-mode>
      <CollapsePanel title="スタイル設定" onChange={() => setIsCollapsed(!isCollapsed)} isCollapsed={isCollapsed}>
        <Box p={2}>
          <Text mb={2} pl={1} fontSize="sm" color="secondary.500" display={hasTextStyles ? 'block' : 'none'}>
            {/* Text */}
            テキスト
          </Text>

          <Box px={2} pl={4}>
            <VStack w="100%">
              {/* Text color */}
              <ColorSetting
                // title="Text Color"
                title="文字色"
                color={currentStyles.textColor}
                defaultColor={DEFAULT_STYLES.textColor}
                visible={availableStyles.includes(DrawingStyle.TextColor)}
                onChange={(clr: string) => {
                  dispatch(setCurrentTextColor(clr))
                }}
                onUpdate={(clr?: string) => {
                  handleUpdateDrawingStyles({ textColor: clr })
                  dispatch(setCurrentTextColor(undefined))
                }}
              />

              {/* Font size */}
              <HStack gap={0} w="100%" display={availableStyles.includes(DrawingStyle.TextSize) ? 'flex' : 'none'}>
                {!isManualEditingTextSize && (
                  <Text mr={2}>
                    {/* Text size */}
                    文字サイズ
                  </Text>
                )}
                <Spacer />
                <RangeInput
                  size="sm"
                  key="fontSize"
                  testId="fontSize"
                  min={1}
                  max={32}
                  step={1}
                  precision={0}
                  value={currentStyles.textSize || 0}
                  defaultValue={currentStyles.textSize || DEFAULT_STYLES.textSize}
                  onDown={() => {
                    shouldPreventExitEditingMode.current = true
                  }}
                  onClose={() => {
                    shouldPreventExitEditingMode.current = false
                  }}
                  onManualEdit={setIsManualEditingTextSize}
                  updateValue={(newValue) => {
                    if (currentStyles.textSize !== newValue) {
                      handleUpdateDrawingStyles({
                        textSize: newValue,
                      })
                    }
                  }}
                  onReset={() => {
                    handleUpdateDrawingStyles({
                      textSize: DEFAULT_STYLES.textSize,
                    })
                  }}
                />
              </HStack>

              {/* Offset */}
              <HStack gap={0} w="100%" display={availableStyles.includes(DrawingStyle.TextOffset) ? 'flex' : 'none'}>
                {!isManualEditingTextOffset && (
                  <Text mr={2}>
                    {/* Offset */}
                    補助線の長さ
                  </Text>
                )}
                <Spacer />
                <RangeInput
                  size="sm"
                  key="textOffset"
                  testId="textOffset"
                  min={MIN_TEXT_OFFSET}
                  max={MAX_TEXT_OFFSET}
                  step={1}
                  precision={0}
                  value={mmToPointsWithScale(currentStyles.textOffset || 0, currentPrintScale)}
                  displayValue={mmToPointsWithScale(currentStyles.textOffset || 0, currentPrintScale).toString()}
                  defaultValue={mmToPointsWithScale(DEFAULT_STYLES.textOffset || 15, currentPrintScale)}
                  unit="pt"
                  onManualEdit={setIsManualEditingTextOffset}
                  updateValue={(newPointsValue) => {
                    if (mmToPointsWithScale(currentStyles.textOffset || 0, currentPrintScale) !== newPointsValue) {
                      const mmValue = pointsToMmWithScale(newPointsValue, currentPrintScale)
                      handleUpdateDrawingStyles({
                        textOffset: mmValue,
                      })
                    }
                  }}
                  onReset={() => {
                    handleUpdateDrawingStyles({
                      textOffset: DEFAULT_STYLES.textOffset,
                    })
                  }}
                />
              </HStack>
            </VStack>
          </Box>

          <hr
            style={{
              borderColor: '{colors.whiteAlpha.200}',
              margin: 'var(--chakra-spacing-2) 0',
              display: hasTextStyles ? 'block' : 'none',
            }}
          />

          <Text mb={2} pl={1} fontSize="sm" color="secondary.500">
            {/* Shape */}
            図形
          </Text>
          <Box px={2} pl={4}>
            <VStack w="100%">
              {/* Stroke color */}

              <ColorSetting
                // title="Text Color"
                title="線の色"
                color={currentStyles.strokeColor}
                defaultColor={DEFAULT_STYLES.strokeColor}
                visible={availableStyles.includes(DrawingStyle.StrokeColor)}
                onChange={(clr: string) => {
                  dispatch(setCurrentStrokeColor(clr))
                }}
                onUpdate={(clr?: string) => {
                  handleUpdateDrawingStyles({ strokeColor: clr })
                  dispatch(setCurrentStrokeColor(undefined))
                }}
              />

              {/* Stroke width */}
              <HStack gap={0} w="100%" display={availableStyles.includes(DrawingStyle.StrokeWidth) ? 'flex' : 'none'}>
                {!isManualEditingStrokeSize && (
                  <Text mr={2}>
                    {/* Stroke Width */}
                    線の太さ
                  </Text>
                )}
                <Spacer />
                <RangeInput
                  size="sm"
                  key="strokeWidth"
                  testId="strokeWidth"
                  min={isPolyline ? 1 : 0}
                  max={100}
                  step={1}
                  precision={0}
                  value={currentStyles.strokeWidth || 0}
                  defaultValue={1}
                  unit="pt"
                  onManualEdit={setIsManualEditingStrokeSize}
                  updateValue={(newValue) => {
                    if (currentStyles.strokeWidth !== newValue) {
                      handleUpdateDrawingStyles({
                        strokeWidth: newValue,
                      })
                    }
                  }}
                  onReset={() => {
                    handleUpdateDrawingStyles({
                      strokeWidth: 1,
                    })
                  }}
                />
              </HStack>

              {/* Stroke style */}
              <HStack gap={0} w="100%" display={availableStyles.includes(DrawingStyle.StrokeStyle) ? 'flex' : 'none'}>
                <Text mr={2}>
                  {/* Stroke Style */}
                  線のスタイル
                </Text>
                <Spacer />
                <Menu.Root
                  variant="toolbar"
                  positioning={{ strategy: 'fixed' }}
                  onOpenChange={(e) => {
                    if (!e.open) {
                      shouldPreventExitEditingMode.current = false
                    }
                  }}
                >
                  <Menu.Trigger asChild>
                    <Button
                      variant="toolbar"
                      fontWeight="normal"
                      fontSize="xs"
                      paddingX={2}
                      height={7}
                      {...INPUT_GROUP_STYLES}
                      onMouseDown={() => {
                        shouldPreventExitEditingMode.current = true
                      }}
                    >
                      {/* 'Dashed' : 'Solid' */}
                      {currentStyles.strokeStyle === LineStyle.Dashed ? '破線' : '実線'}
                      <ChevronDownIcon />
                    </Button>
                  </Menu.Trigger>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content>
                        <Menu.Item
                          value={LineStyle.Solid}
                          onClick={() => handleUpdateDrawingStyles({ strokeStyle: LineStyle.Solid })}
                        >
                          {/* Solid */}
                          実線
                        </Menu.Item>
                        <Menu.Item
                          value={LineStyle.Dashed}
                          onClick={() => handleUpdateDrawingStyles({ strokeStyle: LineStyle.Dashed })}
                        >
                          {/* Dashed */}
                          破線
                        </Menu.Item>
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
                <IconButton
                  aria-label="reset"
                  fontSize="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleUpdateDrawingStyles({ strokeStyle: DEFAULT_STYLES.strokeStyle })
                  }}
                  size="xs"
                  variant="toolbarIcon"
                  data-prevent-exit-editing-mode
                >
                  <ResetIcon />
                </IconButton>
              </HStack>

              {/* Fill color */}
              <ColorSetting
                // title="Text Color"
                title="塗りつぶしの色"
                color={currentStyles.fillColor}
                defaultColor={DEFAULT_STYLES.fillColor}
                visible={availableStyles.includes(DrawingStyle.FillColor)}
                onChange={(clr: string) => {
                  dispatch(setCurrentFillColor(clr))
                }}
                onUpdate={(clr?: string) => {
                  handleUpdateDrawingStyles({ fillColor: clr })
                  dispatch(setCurrentFillColor(undefined))
                }}
              />
            </VStack>
          </Box>
        </Box>
      </CollapsePanel>
    </Box>
  )
}

export default StylesPanel
